import { useCallback, useEffect, useState } from 'react';
import { calendar } from '../services/api';
import {
  buildCalendarGrid,
  filterListDays,
  hasMonthInGrid,
  isMonthDataHydrated,
  mergeMonthIntoGrid,
  type CalendarGridItem,
} from '../utils/calendarGrid';
import {
  formatGregorian,
  getDaysInJalaliMonth,
  jalaliToDate,
  shiftJalaliMonth,
  todayJalali,
} from '../utils/jalali';

export type { CalendarGridItem };

const MONTHS_BEFORE = 3;
const MONTHS_AFTER = 1;

/** Loads multi-month calendar grid for mobile (Ai-Tempo Reset_Calendar parity). */
export function useCalendarData(userId: number | null) {
  const [items, setItems] = useState<CalendarGridItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!userId) {
      setItems([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const today = todayJalali();
      const months: { year: number; month: number }[] = [];
      for (let offset = -MONTHS_BEFORE; offset <= MONTHS_AFTER; offset++) {
        months.push(shiftJalaliMonth(today.year, today.month, offset));
      }
      const first = months[0];
      const last = months[months.length - 1];
      const startDate = formatGregorian(jalaliToDate(first.year, first.month, 1));
      const endDate = formatGregorian(
        jalaliToDate(last.year, last.month, getDaysInJalaliMonth(last.year, last.month)),
      );

      const res = await calendar.getCalendarData(userId, startDate, endDate);
      const apiDays = res.data?.data ?? [];
      setItems(buildCalendarGrid(apiDays));
    } catch (e) {
      setError((e as Error)?.message ?? 'Failed to load calendar');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Fetches one Jalali month when missing or stale (e.g. month expand in weekly view). */
  const ensureMonth = useCallback(
    async (year: number, month: number, options?: { force?: boolean }) => {
      if (!userId) return;

      const shouldSkip = (grid: CalendarGridItem[]) =>
        !options?.force && hasMonthInGrid(grid, year, month) && isMonthDataHydrated(grid, year, month);

      if (shouldSkip(items)) return;

      setLoading(true);
      setError(null);
      try {
        const startDate = formatGregorian(jalaliToDate(year, month, 1));
        const endDate = formatGregorian(
          jalaliToDate(year, month, getDaysInJalaliMonth(year, month)),
        );
        const res = await calendar.getCalendarData(userId, startDate, endDate);
        const apiDays = res.data?.data ?? [];
        setItems((prev) => {
          if (shouldSkip(prev)) return prev;
          return mergeMonthIntoGrid(prev, year, month, apiDays);
        });
      } catch (e) {
        setError((e as Error)?.message ?? 'Failed to load calendar');
      } finally {
        setLoading(false);
      }
    },
    [items, userId],
  );

  const listDays = filterListDays(items);

  return { items, listDays, loading, error, reload: load, ensureMonth };
}
